// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button, Modal } from '@imprivata-cloud/components';
import type { MenuProps } from 'antd';
import { ConfigProvider, Dropdown, Form, message, Table } from 'antd';
import { ChevronDown, Plus } from 'lucide-react';
import SecretCloseWarningModal from '../../../components/secretCloseWarningModal/SecretCloseWarningModal';
import { useCloseWithoutCopyGuard } from '../../../components/secretCloseWarningModal/useCloseWithoutCopyGuard';
import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  IdvAuthMethod,
  type IdvConnection,
  type IdvConnectionCreateRequest,
  type IdvConnectionSummary,
  IdvConnectionTemplate,
  type IdvConnectionUpdateRequest,
  IdvIalLevel,
  IdvScope,
  OidcConnectionType,
} from '../../../api/types';
import { withRole } from '../../../common/authorization/Roles';
import { ErrorCode } from '../../../errorHandler/constants';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import type { ApiError } from '../../../errorHandler/errors';
import { AppError } from '../../../errorHandler/errors';
import { featureSupported } from '../../../featureFlagsUtils';
import { ROLE_TYPE_ADMIN } from '../../constants';
import { useIdvConnections } from './hooks/useIdvConnections';
import { FieldName, type FormValues, TRANSLATION_ROOT } from './IdvConnections.types';
import { buildColumns } from './IdvConnectionsColumns';
import { IdvConnectionForm, IdvSuccessState } from './IdvConnectionsForm';
import { IdvDeleteModal, IdvEmptyState } from './IdvConnectionsModals';
import './Integrations.less';

const truncateForToast = (name: string, max = 30) =>
  name.length > max ? `${name.slice(0, max)}…` : name;

// The backend requires every Custom connection's scope list to include `openid` and rejects
// it otherwise. The form only manages the optional scopes (discovery-driven), so prepend
// `openid` (deduped) when building the wire payload.
const withOpenidScope = (optionalScopes: string[] = []): string[] => [
  ...new Set<string>([IdvScope.OPENID, ...optionalScopes]),
];

// The Custom IDV template is gated until the backend support ships to main. When off,
// the Custom option (and its preceding divider) is dropped from the add-integration menu.
const ENABLE_CUSTOM_IDV_TEMPLATE = featureSupported('ENABLE_CUSTOM_IDV_TEMPLATE');

const SHOW_CLIENT_SECRET_INITIALLY = true;

const IdvConnections = () => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const [messageApi, contextHolder] = message.useMessage();

  const [headerEl, setHeaderEl] = useState<Element | null>(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  useLayoutEffect(() => {
    const header = document.querySelector('.settings-header');
    if (!header) {
      return undefined;
    }
    // Clear any stale portal containers left by a previous mount (StrictMode
    // double-invoke, route re-entry) so only a single "Add integration" button
    // is ever rendered into the header.
    for (const el of header.querySelectorAll('.idv-header-portal')) {
      el.remove();
    }
    const container = document.createElement('div');
    container.className = 'idv-header-portal';
    header.appendChild(container);
    setHeaderEl(container);
    return () => {
      container.remove();
      setHeaderEl(null);
    };
  }, []);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingConnection, setEditingConnection] = useState<IdvConnection | null>(null);
  const [deletingConnection, setDeletingConnection] = useState<IdvConnectionSummary | null>(null);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const [newlyCreatedConnection, setNewlyCreatedConnection] = useState<IdvConnection | null>(null);
  const [createResponse, setCreateResponse] = useState<{
    clientSecret?: string;
    hasSecret: boolean;
  } | null>(null);
  const [showClientSecret, setShowClientSecret] = useState(SHOW_CLIENT_SECRET_INITIALLY);
  const guard = useCloseWithoutCopyGuard();
  const [selectedTemplate, setSelectedTemplate] = useState<IdvConnectionTemplate>(
    IdvConnectionTemplate.EPIC_CARE_LINK,
  );
  const [form] = Form.useForm<FormValues>();

  const {
    connections,
    isLoading,
    reloadConnections,
    fetchConnectionForEdit,
    isEditLoading,
    createConnection,
    updateConnection,
    deleteConnection,
    isDeleting,
    isSubmitting,
    supportedScopes,
  } = useIdvConnections();

  const openAddModal = (template: IdvConnectionTemplate = IdvConnectionTemplate.EPIC_CARE_LINK) => {
    setEditingConnection(null);
    setShowSuccessState(false);
    setNewlyCreatedConnection(null);
    setCreateResponse(null);
    setShowClientSecret(SHOW_CLIENT_SECRET_INITIALLY);
    setSelectedTemplate(template);
    form.resetFields();
    form.setFieldsValue({ [FieldName.REDIRECT_URIS]: [''] });
    if (template === IdvConnectionTemplate.CUSTOM) {
      // Scopes are rendered dynamically from the tenant's discovery doc (`supportedScopes`) and
      // all start UNCHECKED — the admin opts in to each (product decision, 2026-08-24). Only
      // IAL2 is pre-selected, per Figma.
      form.setFieldsValue({ [FieldName.SCOPES]: [], [FieldName.IAL_LEVEL]: IdvIalLevel.IAL2 });
    }
    setIsModalVisible(true);
  };

  const openEditModal = async (summary: IdvConnectionSummary) => {
    try {
      const fullConnection = await fetchConnectionForEdit(summary);

      setSelectedTemplate(fullConnection.templateType);
      setEditingConnection(fullConnection);
      setShowSuccessState(false);
      setNewlyCreatedConnection(null);
      setCreateResponse(null);
      setShowClientSecret(SHOW_CLIENT_SECRET_INITIALLY);

      const redirectUris =
        fullConnection.redirectUris && fullConnection.redirectUris.length > 0
          ? fullConnection.redirectUris
          : [''];

      form.resetFields();
      form.setFieldsValue({
        [FieldName.DISPLAY_NAME]: fullConnection.displayName,
        [FieldName.REDIRECT_URIS]: redirectUris,
        [FieldName.JWKS_URI]: fullConnection.jwksUri,
        ...(fullConnection.templateType === IdvConnectionTemplate.CUSTOM && {
          [FieldName.SCOPES]: fullConnection.scope ?? [],
          [FieldName.AUTH_METHOD]: fullConnection.tokenEndpointAuthMethod,
          [FieldName.IAL_LEVEL]: fullConnection.identityAssuranceLevel,
        }),
      });

      setIsModalVisible(true);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  };

  const forceCloseModal = () => {
    if (showSuccessState && newlyCreatedConnection) {
      void messageApi.success(
        t(`${TRANSLATION_ROOT}.toast.create-success`, {
          displayName: truncateForToast(newlyCreatedConnection.displayName),
        }),
      );
    }
    setIsModalVisible(false);
    setEditingConnection(null);
    setShowSuccessState(false);
    setNewlyCreatedConnection(null);
    setCreateResponse(null);
    setShowClientSecret(SHOW_CLIENT_SECRET_INITIALLY);
    guard.reset();
    form.resetFields();
  };

  const closeModal = () => {
    if (showSuccessState && createResponse?.hasSecret) {
      guard.requestClose(forceCloseModal);
    } else {
      forceCloseModal();
    }
  };

  const setDisplayNameError = () => {
    form.setFields([
      {
        name: FieldName.DISPLAY_NAME,
        errors: [t(`${TRANSLATION_ROOT}.validation.display-name-duplicate`)],
      },
    ]);
  };

  const checkDuplicateDisplayName = (displayName: string, excludeClientId?: string): boolean => {
    return connections.some(
      conn =>
        conn.displayName.toLowerCase() === displayName.toLowerCase() &&
        (!excludeClientId || conn.clientId !== excludeClientId),
    );
  };

  const handleUpdateConnection = async (values: FormValues, redirectUris: string[]) => {
    if (!editingConnection) {
      return;
    }

    if (checkDuplicateDisplayName(values[FieldName.DISPLAY_NAME], editingConnection.clientId)) {
      setDisplayNameError();
      return;
    }

    const updatePayload: IdvConnectionUpdateRequest = {
      clientId: editingConnection.clientId,
      displayName: values[FieldName.DISPLAY_NAME],
      redirectUris,
    };

    if (values[FieldName.JWKS_URI]) {
      updatePayload.jwksUri = values[FieldName.JWKS_URI];
    }

    if (editingConnection.templateType === IdvConnectionTemplate.CUSTOM) {
      updatePayload.scope = withOpenidScope(values[FieldName.SCOPES]);
      if (values[FieldName.IAL_LEVEL]) {
        updatePayload.identityAssuranceLevel = values[FieldName.IAL_LEVEL];
      }
    }

    await updateConnection(updatePayload);
    if (!(await reloadConnections())) {
      return;
    }
    void messageApi.success(
      t(`${TRANSLATION_ROOT}.toast.update-success`, {
        displayName: truncateForToast(values[FieldName.DISPLAY_NAME]),
      }),
    );
    closeModal();
  };

  const handleCreateConnection = async (values: FormValues, redirectUris: string[]) => {
    if (checkDuplicateDisplayName(values[FieldName.DISPLAY_NAME])) {
      setDisplayNameError();
      return;
    }

    const isCustom = selectedTemplate === IdvConnectionTemplate.CUSTOM;

    const payload: IdvConnectionCreateRequest = {
      displayName: values[FieldName.DISPLAY_NAME],
      // The BFF has no "custom" template — Custom connections send an empty templateType
      // ("no template"), which the BFF forwards as an empty product_id. Presets send their value.
      templateType: isCustom ? '' : selectedTemplate,
      integrationType: OidcConnectionType.IDV,
      redirectUris,
    };

    if (values[FieldName.JWKS_URI]) {
      payload.jwksUri = values[FieldName.JWKS_URI];
    }

    if (isCustom) {
      payload.scope = withOpenidScope(values[FieldName.SCOPES]);
      payload.tokenEndpointAuthMethod = values[FieldName.AUTH_METHOD];
      payload.identityAssuranceLevel = values[FieldName.IAL_LEVEL];
    }

    const { response, codingCtx } = await createConnection(payload);

    // private_key_jwt never has a shared secret — Imprivata does not store one for that
    // method, so the success screen must not surface a client secret even if one is returned.
    const usesPrivateKeyJwt = payload.tokenEndpointAuthMethod === IdvAuthMethod.PRIVATE_KEY_JWT;
    const hasSecret = !usesPrivateKeyJwt && !!response.clientSecret;
    const clientSecret =
      hasSecret && response.clientSecret
        ? codingCtx.decryptToString(response.clientSecret)
        : undefined;

    // `response` is already enriched with discovery endpoints by the createConnection mutation.
    const fullConnection: IdvConnection = response;

    // The create response does not reliably echo back the custom fields (scope,
    // tokenEndpointAuthMethod, identityAssuranceLevel). Fall back to the submitted payload
    // values for the success summary whenever the response omits or empties them.
    if (selectedTemplate === IdvConnectionTemplate.CUSTOM) {
      if (!fullConnection.scope?.length) {
        // Strip the implicit `openid` we prepend on the wire — the summary treats openid as
        // implicit (renders it separately) and expects `scope` to hold only optional scopes,
        // matching how normalizeConnection cleans the read path.
        fullConnection.scope = payload.scope?.filter(s => s !== IdvScope.OPENID);
      }
      fullConnection.tokenEndpointAuthMethod ??= payload.tokenEndpointAuthMethod;
      fullConnection.identityAssuranceLevel ??= payload.identityAssuranceLevel;
    }

    setNewlyCreatedConnection(fullConnection);
    setCreateResponse({ clientSecret, hasSecret });
    setShowSuccessState(true);
    void reloadConnections();
  };

  const handleError = (e: unknown) => {
    const err = e as ApiError;
    if (err?.code === ErrorCode.ALREADY_EXISTS) {
      setDisplayNameError();
    } else {
      emitError(new AppError(err));
    }
  };

  const onFinish = async (values: FormValues) => {
    try {
      values[FieldName.DISPLAY_NAME] = values[FieldName.DISPLAY_NAME]?.trim();
      const redirectUris = [
        ...new Set((values[FieldName.REDIRECT_URIS] ?? []).filter(Boolean)),
      ] as string[];

      if (editingConnection) {
        await handleUpdateConnection(values, redirectUris);
      } else {
        await handleCreateConnection(values, redirectUris);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const confirmDelete = async () => {
    if (!deletingConnection) {
      return;
    }
    try {
      await deleteConnection(deletingConnection.clientId);
      if (!(await reloadConnections())) {
        return;
      }
      void messageApi.success(
        t(`${TRANSLATION_ROOT}.toast.delete-success`, {
          displayName: truncateForToast(deletingConnection.displayName),
        }),
      );
      setDeletingConnection(null);
    } catch (e) {
      void messageApi.error(t(`${TRANSLATION_ROOT}.toast.delete-error`));
      emitError(new AppError(e as ApiError));
    }
  };

  const getModalTitle = (): string => {
    if (showSuccessState) {
      return t(`${TRANSLATION_ROOT}.modal.title-add-success`);
    }
    if (editingConnection) {
      return t(`${TRANSLATION_ROOT}.modal.title-edit`, {
        displayName: editingConnection.displayName,
      });
    }
    if (selectedTemplate === IdvConnectionTemplate.EPIC_CARE_LINK) {
      return t(`${TRANSLATION_ROOT}.modal.title-add-epiccare`);
    }
    if (selectedTemplate === IdvConnectionTemplate.SERVICE_NOW) {
      return t(`${TRANSLATION_ROOT}.modal.title-add-servicenow`);
    }
    return t(`${TRANSLATION_ROOT}.modal.title-add-custom`);
  };

  const addIntegrationMenuItems: MenuProps['items'] = [
    {
      key: IdvConnectionTemplate.EPIC_CARE_LINK,
      label: 'EpicCare Link identity verification',
      onClick: () => openAddModal(IdvConnectionTemplate.EPIC_CARE_LINK),
    },
    {
      key: IdvConnectionTemplate.SERVICE_NOW,
      label: 'ServiceNow identity verification',
      onClick: () => openAddModal(IdvConnectionTemplate.SERVICE_NOW),
    },
    // Custom is gated behind ENABLE_CUSTOM_IDV_TEMPLATE until backend support lands in main.
    // A divider separates the generic "Custom" option from the branded presets, per Figma.
    ...(ENABLE_CUSTOM_IDV_TEMPLATE
      ? ([
          { type: 'divider' },
          {
            key: IdvConnectionTemplate.CUSTOM,
            label: 'Custom identity verification',
            onClick: () => openAddModal(IdvConnectionTemplate.CUSTOM),
          },
        ] satisfies MenuProps['items'])
      : []),
  ];

  return (
    <>
      {headerEl &&
        createPortal(
          <ConfigProvider wave={{ disabled: true }}>
            <Dropdown
              menu={{ items: addIntegrationMenuItems }}
              disabled={isLoading}
              overlayClassName="idv-integration-dropdown idv-empty-state-dropdown"
              trigger={['click']}
              placement="bottomRight"
              open={headerMenuOpen}
              onOpenChange={setHeaderMenuOpen}
            >
              <Button
                disabled={isLoading}
                className="add-integration-button"
                data-button-type="Secondary"
                data-button-state="Enabled"
                data-button-size="Medium"
              >
                <Plus size={16} className="icon-left" />
                <span className="button-label">{t(`${TRANSLATION_ROOT}.add-connection-cta`)}</span>
                <ChevronDown
                  size={16}
                  className={headerMenuOpen ? 'icon-right open' : 'icon-right'}
                />
              </Button>
            </Dropdown>
          </ConfigProvider>,
          headerEl,
        )}
      <div className="integrations-container">
        {contextHolder}
        <div className="idv-connections-table-scroll">
          {connections.length === 0 && !isLoading ? (
            <IdvEmptyState isLoading={isLoading} addMenuItems={addIntegrationMenuItems} />
          ) : (
            <Table<IdvConnectionSummary>
              className="idv-connections-table"
              dataSource={connections}
              rowKey="clientId"
              loading={isLoading || isEditLoading}
              pagination={false}
              showHeader={false}
              tableLayout="fixed"
              // The empty-state branch above owns the loaded-and-empty case, so this Table
              // only ever renders with zero rows mid-load — blank the placeholder so the
              // loading spinner shows instead of antd's "No data" text.
              locale={{ emptyText: <span className="idv-connections-loading-placeholder" /> }}
              columns={buildColumns(
                t,
                isLoading || isEditLoading,
                openEditModal,
                (conn: IdvConnectionSummary) => setDeletingConnection(conn),
              )}
            />
          )}
        </div>

        <Modal
          title={getModalTitle()}
          open={isModalVisible}
          width={859}
          destroyOnHidden
          onCancel={closeModal}
          footer={null}
          maskClosable={false}
          aria-labelledby="idv-modal-title"
          className="idv-connection-modal"
          zIndex={1000}
          afterOpenChange={open => {
            if (open && !showSuccessState && !editingConnection) {
              document.querySelector<HTMLInputElement>('#idvConnection input')?.focus();
            }
          }}
        >
          {showSuccessState && newlyCreatedConnection ? (
            <IdvSuccessState
              connection={newlyCreatedConnection}
              clientSecret={createResponse?.clientSecret}
              showClientSecret={showClientSecret}
              onShowClientSecretChange={setShowClientSecret}
              onDone={closeModal}
              onSecretCopied={guard.markSecretCopied}
            />
          ) : (
            <IdvConnectionForm
              form={form}
              editingConnection={editingConnection}
              selectedTemplate={selectedTemplate}
              isLoading={isSubmitting}
              onCancel={closeModal}
              onFinish={onFinish}
              supportedScopes={supportedScopes}
            />
          )}
        </Modal>

        <SecretCloseWarningModal
          open={guard.isWarningOpen}
          onConfirm={guard.confirmClose}
          onCancel={guard.cancelClose}
          title={t(`${TRANSLATION_ROOT}.modal.close-without-copy-title`)}
          message={t(`${TRANSLATION_ROOT}.modal.close-without-copy-body`)}
          confirmLabel={t(`${TRANSLATION_ROOT}.modal.close-without-copy-confirm`)}
          cancelLabel={t(`${TRANSLATION_ROOT}.modal.close-without-copy-cancel`)}
        />

        <IdvDeleteModal
          connection={deletingConnection}
          isLoading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeletingConnection(null)}
        />
      </div>
    </>
  );
};

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(IdvConnections);
